import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { titleCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import { Heading, Text } from '@frontend/design-system';

type InsuranceStatusProps = {
  lastVerifiedDate: string;
  status: string;
  effectiveDate?: string;
  endDate?: string;
  isReportWriteback?: boolean;
};

export const InsuranceStatus = ({
  lastVerifiedDate,
  status,
  effectiveDate,
  endDate,
  isReportWriteback = false,
}: InsuranceStatusProps) => {
  const { t } = useTranslation('insuranceEligibilityReport');

  if (isEmpty(status) || isEmpty(lastVerifiedDate)) return null;

  const lastVerificationDate = lastVerifiedDate ? formatDate(lastVerifiedDate, 'MM/DD/YYYY') : '';
  const daysDifference = lastVerifiedDate ? dayjs().diff(dayjs(lastVerificationDate), 'day') : 0;
  const numberOfDays = Math.abs(daysDifference);

  return (
    <div css={insuranceStatusContainerStyles}>
      <div css={statusContainerStyles}>
        <div css={statusStyles}>
          <Heading level={2}>{t('Status:')}</Heading>
          <StatusBadge shouldRenderIcon={!isReportWriteback} status={status || t('Active')} />
        </div>
        <div>
          <InsuranceDetailsDates effectiveDate={effectiveDate} endDate={endDate} />
        </div>
      </div>
      {lastVerificationDate && (
        <div css={lastVerifiedContainerStyles}>
          <Heading level={3}>{t('Last Verified On:')}</Heading>
          <Heading level={3}>{lastVerificationDate}</Heading>
          <Text size='large' color='subdued'>
            {t('({{count}} days back)', { count: numberOfDays })}
          </Text>
        </div>
      )}
    </div>
  );
};

export const insuranceStatusContainerStyles = css({
  borderRadius: theme.borderRadius.medium,
  border: `1px solid ${theme.colors.neutral20}`,
  backgroundColor: theme.colors.white,
});

const statusStyles = css({ display: 'flex', alignItems: 'center', gap: theme.spacing(1) });

const lastVerifiedContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  background: theme.colors.neutral5,
  padding: theme.spacing(2),
  borderRadius: theme.borderRadius.medium,
});

type InsuranceDetailsDatesProps = {
  effectiveDate?: string;
  endDate?: string;
};
const InsuranceDetailsDates = ({ effectiveDate, endDate }: InsuranceDetailsDatesProps) => {
  const { t } = useTranslation('insuranceEligibilityReport');
  if (!effectiveDate && !endDate) return null;

  return (
    <section css={insuranceDatesStyles}>
      {!!effectiveDate && <InsuranceDates label={t('Effective Date')} dateStringValue={effectiveDate} />}
      {!!endDate && <InsuranceDates label={t('End Date')} dateStringValue={endDate} />}
    </section>
  );
};

const insuranceDatesStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
  > :last-child::before {
    content: ' | ';
    padding: 0 ${theme.spacing(0.5)};
    color: ${theme.colors.neutral30};
  }
`;

type InsuranceDatesProps = {
  label: string;
  dateStringValue: string;
};

const InsuranceDates = ({ label, dateStringValue }: InsuranceDatesProps) => {
  if (!dateStringValue || !label) return null;
  return (
    <section css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
      <Text weight='bold' size='large'>
        {label}:
      </Text>
      <Text color='subdued' weight='bold'>
        {dateStringValue}
      </Text>
    </section>
  );
};

type StatusBadgeProps = {
  status: string;
  shouldRenderIcon?: boolean;
};
const StatusBadge = ({ status, shouldRenderIcon = true }: StatusBadgeProps) => {
  const isActiveStatus = status?.toLowerCase() === 'active';
  return (
    <div css={statusBadgeStyles(status)}>
      {shouldRenderIcon && <Icon name={isActiveStatus ? 'shield' : 'insurance-uninsured'} color='white' />}
      <Text color='white' weight='semibold'>
        {titleCase(status)}
      </Text>
    </div>
  );
};

const statusBadgeStyles = (status: string) => {
  return css({
    display: 'flex',
    padding: theme.spacing(0.5, 1),
    backgroundColor: status?.toLowerCase() === 'active' ? theme.colors.success50 : theme.colors.critical50,
    width: 'fit-content',
    borderRadius: theme.borderRadius.medium,
    alignItems: 'center',
    gap: theme.spacing(0.5),
  });
};

const statusContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
});
